<script setup>
import { ref, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import ModalBox from './ModalBox.vue'
import Spinner from '@/components/ui/Spinner.vue'
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
	modalId: {
		type: String,
		default: 'AddPhoneNumberModal',
	},
	modalSize: {
		type: String,
		default: 'sm',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	stream: {
		type: Object,
		required: true,
	},
})

const emit = defineEmits(['user-contact-created'])

const { notify } = useNotification()

const processing = ref(false)
const validationError = ref(false)
const errorMsg = ref('')
const phoneNumberInput = ref(null)
const phoneNumberValid = ref(false)
const phoneFormattedNumber = ref(null)
const telOptions = {
	showDialCodeInList: true,
	showDialCodeInSelection: true,
	showFlags: true,
	showSearchBox: true,
}

const { isVisible, hide } = useLegacyModalBridge(() => props.modalId, {
	onHidden: () => {
		processing.value = false
		validationError.value = false
	},
})

onMounted(() => {

})

const onValidate = (mobile) => {
	validationError.value = false
	if (!mobile.valid) {
		phoneNumberValid.value = false
		return
	}
	phoneFormattedNumber.value = mobile.number
	phoneNumberValid.value = true
}

const dismiss = () => {
	hide()
}

const onSave = async () => {
	processing.value = true
	try {
		const contact = await UserService.createUserContact(phoneFormattedNumber.value)
		contact.contact.id = contact._id
		track(EVENTS.PHONE_NUMBER_ADDED, {})
		emit('user-contact-created', contact)
		hide()
	} catch (e) {
		notify({ group: 'error', text: e?.message || 'Failed to save phone number' })
		errorMsg.value = e?.message || 'Failed to save phone number'
		validationError.value = true
		setTimeout(() => {
			validationError.value = false
		}, 5000)
	}
	processing.value = false
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		@close="dismiss"
	>
		<div class="px-6 pt-6">
			<h4 class="text-s-xl mb-2">Add phone number</h4>
			<p class="text-s-s text-dark-8 mb-4">
				Castr will send SMS notifications to the number you added.
			</p>
			<vue-tel-input
				class="phone-input"
				v-model="phoneNumberInput"
				mode="national"
				auto-format
				:dropdown-options="telOptions"
				@validate="onValidate"
			/>
			<span v-if="validationError" class="text-s-s mt-2 block" style="color: #ba2929;">{{ errorMsg }}</span>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="secondary" size="md" @click="dismiss">
					Cancel
				</FormButton>
				<FormButton type="main" size="md" :disabled="!phoneNumberValid || processing" @click="onSave">
					Add Number
					<Spinner
						v-if="processing"
						text=""
						classes="text-dark-8 ml-2"
						spinner-color="var(--c-dark-8)"
						spinner-size="15px"
					/>
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>

<style>
.country-selector__input {
	border: 0px solid #ccc;
}
.input-tel__input {
	border: 0px solid #ccc;
}
</style>
